"use client";

import React, { useEffect, useState } from "react";
import { cn } from "../util/cn";
import Image from "next/image";
import { AnimatePresence, motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, ChevronLeftIcon, ChevronRightIcon } from "@hugeicons/core-free-icons";

export const InfiniteMovingCards = ({
  items,
  direction = "left",
  speed = "slow",
  pauseOnHover = true,
  className,
}: {
  items: {
    image: string;
    title: string;
  }[];
  direction?: "left" | "right";
  speed?: "fast" | "normal" | "slow";
  pauseOnHover?: boolean;
  className?: string;
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollerRef = React.useRef<HTMLUListElement>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);

  useEffect(() => {
    addAnimation();
  }, []);

  const [start, setStart] = useState(false);
  function addAnimation() {
    if (containerRef.current && scrollerRef.current) {
      const scrollerContent = Array.from(scrollerRef.current.children);

      scrollerContent.forEach((item) => {
        const duplicatedItem = item.cloneNode(true);
        if (scrollerRef.current) {
          scrollerRef.current.appendChild(duplicatedItem);
        }
      });

      getDirection();
      getSpeed();
      setStart(true);
    }
  }
  const getDirection = () => {
    if (containerRef.current) {
      if (direction === "left") {
        containerRef.current.style.setProperty(
          "--animation-direction",
          "forwards",
        );
      } else {
        containerRef.current.style.setProperty(
          "--animation-direction",
          "reverse",
        );
      }
    }
  };
  const getSpeed = () => {
    if (containerRef.current) {
      if (speed === "fast") {
        containerRef.current.style.setProperty("--animation-duration", "40s");
      } else if (speed === "normal") {
        containerRef.current.style.setProperty("--animation-duration", "80s");
      } else {
        containerRef.current.style.setProperty("--animation-duration", "260s");
      }
    }
  };

  // Gallery Navigation Functions
  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (activeImageIndex !== null) {
      setActiveImageIndex((prev) =>
        prev === null ? null : (prev - 1 + items.length) % items.length,
      );
    }
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (activeImageIndex !== null) {
      setActiveImageIndex((prev) =>
        prev === null ? null : (prev + 1) % items.length,
      );
    }
  };

  const handleClose = () => {
    setActiveImageIndex(null);
  };

  const handleCardClick = (e: React.MouseEvent<HTMLUListElement>) => {
    const target = e.target as HTMLElement;
    const li = target.closest("li");
    if (li) {
      const indexAttr = li.getAttribute("data-index");
      if (indexAttr !== null) {
        setActiveImageIndex(parseInt(indexAttr, 10));
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (activeImageIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      else if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeImageIndex]);

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          "scroller relative z-20 min-w-full overflow-hidden mask-[linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]",
          className,
        )}
      >
        <ul
          ref={scrollerRef}
          onClick={handleCardClick}
          className={cn(
            "flex min-w-full shrink-0 gap-4 py-4 w-max flex-nowrap",
            start && "animate-scroll",
            pauseOnHover && "hover:paused",
          )}
        >
          {items.map((item, idx) => (
            <li
              data-index={idx}
              className="group/card relative h-[220px] md:h-[350px] aspect-640/319 shrink-0 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 cursor-pointer transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
              key={item.title + idx}
            >
              <div className="absolute inset-0 h-full w-full">
                <Image
                  src={item.image}
                  alt={item.title}
                  fill
                  className="object-cover pointer-events-none"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
              </div>

              {/* Hover overlay with localized tooltip */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 flex items-center justify-center z-30 pointer-events-none">
                <span className="text-xs md:text-sm font-outfit text-white bg-black/70 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-lg tracking-wide">
                  Click to View
                </span>
              </div>

              <div className="relative z-20 h-full flex flex-col justify-end p-6 pointer-events-none">
                <span className="text-xl font-outfit md:text-2xl font-bold text-white tracking-wide">
                  {item.title}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <AnimatePresence>
        {activeImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-200 flex flex-col items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-8 cursor-zoom-out"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-6 right-6 z-201 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
              aria-label="Close gallery"
            >
              <HugeiconsIcon icon={Cancel01Icon} className="w-6 h-6" />
            </button>

            {/* Navigation Arrow Left */}
            <button
              onClick={handlePrev}
              className="absolute left-4 sm:left-8 z-201 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
              aria-label="Previous image"
            >
              <HugeiconsIcon icon={ChevronLeftIcon} className="w-6 h-6" />
            </button>

            {/* Main Content Area */}
            <div className="relative w-full max-w-[950px] aspect-video max-h-[65vh] flex flex-col items-center justify-center mb-24 sm:mb-28">
              <motion.div
                key={activeImageIndex}
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -15 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="relative w-full h-full cursor-default select-none"
                onClick={(e) => e.stopPropagation()}
              >
                <Image
                  src={items[activeImageIndex].image}
                  alt={items[activeImageIndex].title}
                  fill
                  className="object-contain rounded-3xl"
                  priority
                />
              </motion.div>
            </div>

            {/* Navigation Arrow Right */}
            <button
              onClick={handleNext}
              className="absolute right-4 sm:right-8 z-201 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
              aria-label="Next image"
            >
              <HugeiconsIcon icon={ChevronRightIcon} className="w-6 h-6" />
            </button>

            {/* Thumbnails Grid/Row */}
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 p-3 pb-4 rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 max-w-[90%] overflow-x-auto gallery-scrollbar z-201"
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className={cn(
                    "relative h-10 sm:h-14 aspect-video rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95 shrink-0",
                    activeImageIndex === idx
                      ? "border-white scale-105 shadow-md shadow-white/10"
                      : "border-transparent opacity-50 hover:opacity-100",
                  )}
                >
                  <Image
                    src={item.image}
                    alt={`Thumbnail ${idx + 1}`}
                    fill
                    className="object-cover"
                  />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

